window.dataLayer = window.dataLayer || [];

const commonData = {
  page_type: window.getPageType(),
  user_id: window.getUserId(),
  user_status: window.getUserStatus(),
};

const GTMTracking = {
  push(eventName, ecommerceData) {
    window.dataLayer.push({ ecommerce: null }); // clear previous ecommerce
    window.dataLayer.push({
      event: eventName,
      ...commonData,
      ecommerce: ecommerceData,
    });
  },

  observeView(selector, callback, threshold = 0.5) {
    const elements = document.querySelectorAll(selector);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            callback(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold }
    );
    elements.forEach((el) => observer.observe(el));
  },

  onClick(selector, callback) {
    document.addEventListener("click", function (e) {
      const target = e.target.closest(selector);
      if (target) callback(target, e);
    });
  },
};

window.GTMTracking = window.GTMTracking || {};

window.GTMTracking.viewPromotion = function (uniqueIdentity, items) {
  GTMTracking.observeView(uniqueIdentity, () => {
    GTMTracking.push("view_promotion", { items });
  });
};

window.GTMTracking.clickPromotion = function (uniqueIdentity, items) {
  GTMTracking.clickPromotion(uniqueIdentity, () => {
    GTMTracking.push("select_promotion", { items });
  });
};

window.GTMTracking.viewItemList = function (options = {}) {
  /**
   * Tracks GA4 view_item_list for any page with product cards.
   *
   * @param {Object} options
   * @param {string} [options.cardSelector='[data-product-card]'] - Selector for product cards
   * @param {string} [options.itemListName='Search Results'] - Name of the product list
   * @param {string} [options.itemListId='search_results'] - ID of the product list
   * @param {string} [options.listContainerSelector='[data-product-list-container]'] - Selector for product list container
   * @param {string} [options.timeout='number']
   * @param {string} [options.trackAllProducts='boolean'] - Whether to track all products on the page or only those in viewport
   */
  const trackAllProducts = options?.trackAllProducts || false;
  const cardSelector = options?.cardSelector || "[data-product-card]";
  const listContainerSelector =
    options?.listContainerSelector || "[data-product-list-container]";

  const productListContainer = document.querySelector(listContainerSelector);

  const getProductCards = () => {
    if (productListContainer) {
      return productListContainer.querySelectorAll(cardSelector);
    } else if (trackAllProducts) {
      return document.querySelectorAll(cardSelector);
    }
    return [];
  };

  function getProductData(card, index) {
    return {
      item_id: card.getAttribute("data-product-id"),
      item_name: card.getAttribute("data-product-title"),
      coupon: card.getAttribute("data-product-coupon") || "NA",
      discount: parseFloat(card.getAttribute("data-product-discount")) || 0,
      index: index + 1,
      item_brand: card.getAttribute("data-brand"),
      item_category: card.getAttribute("data-product-category"),
      item_category2: card.getAttribute("data-product-category2"),
      item_category3: card.getAttribute("data-product-category3"),
      item_category4: card.getAttribute("data-product-category4") || "NA",
      item_category5: card.getAttribute("data-product-category5"),
      item_list_id:
        card.getAttribute("data-product-list-id") ||
        options?.itemListId ||
        "NA",
      item_list_name:
        card.getAttribute("data-product-list-name") ||
        options?.itemListName ||
        "NA",
      item_variant: card.getAttribute("data-product-variant") || "NA",
      price: parseFloat(card.getAttribute("data-product-price")),
      quantity: 1,
    };
  }

  const seen = new Set();
  const pending = new Map();
  let flushScheduled = false;

  const isPartiallyInViewport = (el) => {
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    return rect.bottom > 0 && rect.top < vh && rect.right > 0 && rect.left < vw;
  };

  let lastFlushTime = 0;
  const FLUSH_DEBOUNCE_MS = 100;
  const flush = () => {
    flushScheduled = false;
    const now = Date.now();
    if (now - lastFlushTime < FLUSH_DEBOUNCE_MS) {
      // Debounce rapid flushes
      scheduleFlush();
      return;
    }
    lastFlushTime = now;
    const cardsNow = Array.from(pending.values()).filter(isPartiallyInViewport);
    if (cardsNow.length === 0) return;

    // Only flush cards that have not been seen before
    const allCards = Array.from(getProductCards());
    cardsNow.sort((a, b) => allCards.indexOf(a) - allCards.indexOf(b));

    const items = cardsNow.map((card) => {
      const globalIndex = allCards.indexOf(card);
      return getProductData(card, globalIndex);
    });

    if (items.length > 0) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "view_item_list",
        ...commonData,
        ecommerce: { items },
      });
    }

    cardsNow.forEach((card) => {
      const id = card.getAttribute("data-product-id");
      pending.delete(id);
      seen.add(id); // Ensure seen is always updated here
    });
  };

  const scheduleFlush = () => {
    if (flushScheduled) return;
    flushScheduled = true;
    requestAnimationFrame(() => setTimeout(flush, options?.timeout || 0));
  };

  const io = new IntersectionObserver(
    (entries) => {
      let anyNew = false;
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const card = entry.target;
        const id = card.getAttribute("data-product-id");
        if (seen.has(id)) {
          io.unobserve(card);
          return;
        }
        seen.add(id);
        pending.set(id, card);
        anyNew = true;
        io.unobserve(card);
      });
      if (anyNew) scheduleFlush();
    },
    { threshold: 0.3 }
  );

  function observeNewCards() {
    getProductCards().forEach((card) => {
      const id = card.getAttribute("data-product-id");
      if (!seen.has(id) && !pending.has(id)) {
        io.observe(card);
      }
    });
  }

  observeNewCards();

  // Only observe new cards on infinite scroll, do not schedule flush here
  document.addEventListener("infiniteLoadComplete", () => {
    observeNewCards();
  });

  const mo = new MutationObserver((mutations) => {
    let foundNew = false;
    mutations.forEach((m) => {
      m.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches?.(cardSelector)) foundNew = true;
        else if (node.querySelector?.(cardSelector)) foundNew = true;
      });
    });
    if (foundNew) {
      observeNewCards();
      // Do not call scheduleFlush here to avoid duplicate events
    }
  });

  mo.observe(productListContainer, { childList: true, subtree: true });
};

window.GTMTracking.viewTabItemList = function (sectionContainer) {
  if (!sectionContainer) return;

  function buildItemObj(card, index, tabName) {
    return {
      item_id: card.getAttribute("data-product-id"),
      item_name: card.getAttribute("data-product-title"),
      coupon: card.getAttribute("data-product-coupon") || "NA",
      discount: parseFloat(card.getAttribute("data-product-discount")) || 0,
      index: index + 1,
      item_brand: card.getAttribute("data-brand"),
      item_category: card.getAttribute("data-product-category"),
      item_category2: card.getAttribute("data-product-category2"),
      item_category3: card.getAttribute("data-product-category3"),
      item_category4: card.getAttribute("data-product-category4") || "NA",
      item_category5: card.getAttribute("data-product-category5"),
      item_list_id: tabName
        ? tabName?.toLowerCase()?.replace(/\s+/g, "_")
        : "NA",
      item_list_name: tabName || "NA",
      item_variant: card.getAttribute("data-product-variant") || "NA",
      price: parseFloat(card.getAttribute("data-product-price")),
      quantity: 1,
    };
  }

  function trackVisibleProducts(productCards, tabName) {
    if (!productCards.length) return;

    const items = productCards.map((card, index) =>
      buildItemObj(card, index, tabName)
    );

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: "view_item_list",
      ...commonData,
      ecommerce: { items },
    });
  }

  function observeProducts(tabContentElement, tabName) {
    const productCards = tabContentElement.querySelectorAll(
      "[data-product-card]"
    );
    if (!productCards.length) return;

    const visibleProducts = new Set();

    const productObserver = new IntersectionObserver(
      (entries) => {
        const newlyVisible = [];
        entries.forEach((entry) => {
          if (entry.isIntersecting && !visibleProducts.has(entry.target)) {
            visibleProducts.add(entry.target);
            newlyVisible.push(entry.target);
          }
        });

        if (newlyVisible.length) {
          trackVisibleProducts(newlyVisible, tabName);
        }
      },
      { threshold: 0.5 }
    );

    productCards.forEach((card) => productObserver.observe(card));
  }

  // --- Tab buttons + panels ---
  const tabControls = sectionContainer.querySelectorAll(
    "button.hdt-tabs-item-title"
  );
  const tabPanels = sectionContainer.querySelectorAll(".hdt-tab-item");

  // --- CLICK EVENT TRACKING ---
  tabControls.forEach((tabControl, index) => {
    tabControl.addEventListener("click", function () {
      const currentTabName = tabControl.textContent.trim();
      setTimeout(() => {
        const panelToTrack = tabPanels[index];
        if (panelToTrack) {
          observeProducts(panelToTrack, currentTabName);
        }
      }, 200);
    });
  });

  // --- INITIAL TAB VISIBILITY TRACKING ---
  const observerCallback = (entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const initialActiveTabPanel = sectionContainer.querySelector(
          ".hdt-tab-item[tab-selected]"
        );
        const initialActiveButton = sectionContainer.querySelector(
          'button.hdt-tabs-item-title[aria-current="true"]'
        );
        const initialTabName = initialActiveButton
          ? initialActiveButton.textContent.trim()
          : "Initial_Products";
        if (initialActiveTabPanel) {
          observeProducts(initialActiveTabPanel, initialTabName);
        }
        observer.unobserve(sectionContainer);
      }
    });
  };

  const intersectionObserver = new IntersectionObserver(observerCallback, {
    threshold: 0.1,
  });
  intersectionObserver.observe(sectionContainer);
};

// PDP - View Item List for Related Products & Recently Viewed Products Start
(function (window, document) {
  const seenProducts = {};

  function buildItemObj(card, index, tabName) {
    return {
      item_id: card.getAttribute("data-product-id"),
      item_name: card.getAttribute("data-product-title"),
      coupon: card.getAttribute("data-product-coupon") || "NA",
      discount: parseFloat(card.getAttribute("data-product-discount")) || 0,
      index: index + 1,
      item_brand: card.getAttribute("data-brand"),
      item_category: card.getAttribute("data-product-category"),
      item_category2: card.getAttribute("data-product-category2"),
      item_category3: card.getAttribute("data-product-category3"),
      item_category4: card.getAttribute("data-product-category4") || "NA",
      item_category5: card.getAttribute("data-product-category5"),
      item_list_id: tabName ? tabName.toLowerCase().replace(/\s+/g, "_") : "NA",
      item_list_name: tabName || "NA",
      item_variant: card.getAttribute("data-product-variant") || "NA",
      price: parseFloat(card.getAttribute("data-product-price")),
      quantity: 1,
    };
  }

  function fireViewItems(cards, tabName) {
    if (!cards.length) return;

    const items = cards.map((card, i) => buildItemObj(card, i, tabName));

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: "view_item_list",
      ...commonData,
      ecommerce: { items: items },
    });
  }

  function observeProducts(sectionEl, tabName) {
    if (!seenProducts[tabName]) seenProducts[tabName] = new Set();

    const cards = sectionEl.querySelectorAll(
      ".hdt-slider__slide[data-product-id]"
    );
    if (!cards.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const newlyVisible = [];
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const card = entry.target;
            const pid = card.getAttribute("data-product-id");
            if (!seenProducts[tabName].has(pid)) {
              seenProducts[tabName].add(pid);
              newlyVisible.push(card);
            }
          }
        });
        if (newlyVisible.length) {
          fireViewItems(newlyVisible, tabName);
        }
      },
      { threshold: 0.5 }
    );

    cards.forEach((card) => observer.observe(card));
  }

  function initTab(tabContentEl, tabName) {
    // Reset seen products for this tab
    seenProducts[tabName] = new Set();

    const mo = new MutationObserver(() => {
      if (tabContentEl.querySelector(".hdt-slider__slide[data-product-id]")) {
        observeProducts(tabContentEl, tabName);
        mo.disconnect();
      }
    });
    mo.observe(tabContentEl, { childList: true, subtree: true });
  }

  // Expose as global function
  window.GTMTracking = window.GTMTracking || {};
  window.GTMTracking.viewTabItemListForPDP = function (
    relatedSelector,
    recentlySelector,
    sectionId,
    relatedHeading
  ) {
    const relatedTab = document.querySelector(relatedSelector);
    const recentlyTab = document.querySelector(recentlySelector);

    if (relatedTab) initTab(relatedTab, relatedHeading);

    document
      .querySelectorAll(`#Scroller-${sectionId} button`)
      .forEach((btn) => {
        btn.addEventListener("click", function () {
          const tabName = btn.textContent.trim();
          const targetTab =
            tabName === relatedHeading ? relatedTab : recentlyTab;
          if (targetTab) initTab(targetTab, tabName);
        });
      });
  };
})(window, document);
// PDP - View Item List for Related Products & Recently Viewed Products end

window.GTMTracking.viewItem = function (items) {
  GTMTracking.push("view_item", items);
};

let currentProductEcomData = null;

document.addEventListener("DOMContentLoaded", function () {
  document.body.addEventListener(
    "click",
    function (e) {
      const el = e.target.closest("[data-ecom-gtm]");
      if (!el) return;
      const event = el.getAttribute("data-ecom-gtm") || "NA";
      const heading = el.getAttribute("data-heading") || "NA";
      const promotionName = el.getAttribute("data-promotion-name") || "NA";
      const creativeSlot = el.getAttribute("data-creative-slot") || "NA";
      const promotionId = el.getAttribute("data-promotion-id") || "NA";

      if (event === "select_promotion") {
        window.dataLayer.push({
          event: "select_promotion",
          ...commonData,
          ecommerce: {
            items: [
              {
                creative_name: heading,
                creative_slot: creativeSlot,
                promotion_id: promotionId,
                promotion_name: promotionName ?? heading,
              },
            ],
          },
        });
      }
      if (event === "select_item") {
        window.dataLayer.push({
          event: "select_item",
          ...commonData,
          ecommerce: {
            items: [
              {
                item_id: el.getAttribute("data-product-id"),
                item_name: el.getAttribute("data-product-title"),
                coupon: el.getAttribute("data-product-coupon") || "NA",
                discount:
                  parseFloat(el.getAttribute("data-product-discount")) || 0,
                index: index + 1,
                item_brand: el.getAttribute("data-brand"),
                item_category: el.getAttribute("data-product-category"),
                item_category2: el.getAttribute("data-product-category2"),
                item_category3: el.getAttribute("data-product-category3"),
                item_category4:
                  el.getAttribute("data-product-category4") || "NA",
                item_category5: el.getAttribute("data-product-category5"),
                item_list_id: el.getAttribute("data-product-list-id"),
                item_list_name: el.getAttribute("data-product-list-name"),
                item_variant: el.getAttribute("data-product-variant") || "NA",
                price: parseFloat(el.getAttribute("data-product-price")),
                quantity: 1,
              },
            ],
          },
        });
      }
      if (event === "add_to_cart") {
        currentProductEcomData = {
          currency: el.getAttribute("data-currency"),
          value: parseFloat(el.getAttribute("data-product-price")),
          items: [
            {
              item_id: el.getAttribute("data-product-id"),
              item_name: el.getAttribute("data-product-title"),
              coupon: el.getAttribute("data-product-coupon") || "NA",
              discount:
                parseFloat(el.getAttribute("data-product-discount")) || 0,
              index: index + 1,
              item_brand: el.getAttribute("data-brand"),
              item_category: el.getAttribute("data-product-category"),
              item_category2: el.getAttribute("data-product-category2"),
              item_category3: el.getAttribute("data-product-category3"),
              item_category4: el.getAttribute("data-product-category4") || "NA",
              item_category5: el.getAttribute("data-product-category5"),
              item_list_id: el.getAttribute("data-product-list-id"),
              item_list_name: el.getAttribute("data-product-list-name"),
              item_variant: el.getAttribute("data-product-variant") || "NA",
              price: parseFloat(el.getAttribute("data-product-price")),
              quantity: 1,
            },
          ],
        };
      }
      if (event === "add_to_wishlist") {
        window.dataLayer.push({
          event: event,
          ...commonData,
          ecommerce: {
            currency: el.getAttribute("data-currency"),
            value: parseFloat(el.getAttribute("data-product-price")),
            items: [
              {
                item_id: el.getAttribute("data-product-id"),
                item_name: el.getAttribute("data-product-title"),
                coupon: el.getAttribute("data-product-coupon") || "NA",
                discount:
                  parseFloat(el.getAttribute("data-product-discount")) || 0,
                index: index + 1,
                item_brand: el.getAttribute("data-brand"),
                item_category: el.getAttribute("data-product-category"),
                item_category2: el.getAttribute("data-product-category2"),
                item_category3: el.getAttribute("data-product-category3"),
                item_category4:
                  el.getAttribute("data-product-category4") || "NA",
                item_category5: el.getAttribute("data-product-category5"),
                item_list_id: el.getAttribute("data-product-list-id"),
                item_list_name: el.getAttribute("data-product-list-name"),
                item_variant: el.getAttribute("data-product-variant") || "NA",
                price: parseFloat(el.getAttribute("data-product-price")),
                quantity: 1,
              },
            ],
          },
        });
      }
    },
    true
  );

  // HS - Slide Cart Drawer events start
  let latestCartObject = null;
  window.HS_SLIDE_CART_LOADED = function (cart) {
    latestCartObject = cart;
  };

  function findCartChanges(oldCart, newCart) {
    const changes = [];
    const oldCartMap = new Map(oldCart.map((item) => [item.id, item]));
    const newCartMap = new Map(newCart.map((item) => [item.id, item]));

    // --- New flags to track the overall action ---
    let isAddToCartAction = false;
    let isRemoveItemAction = false;

    // Check for added or updated (increased/decreased quantity) items
    for (const [id, newItem] of newCartMap.entries()) {
      const oldItem = oldCartMap.get(id);

      if (!oldItem) {
        // Case 1: Item was added
        changes.push({ type: "Added", item: newItem });
        isAddToCartAction = true; // Set the flag
      } else {
        if (newItem.quantity > oldItem.quantity) {
          // Case 3: Quantity was increased
          const quantityChange = newItem.quantity - oldItem.quantity;
          changes.push({ type: "Increased", quantityChange, item: newItem });
          isAddToCartAction = true; // Set the flag
        } else if (newItem.quantity < oldItem.quantity) {
          // Case 4: Quantity was decreased
          const quantityChange = oldItem.quantity - newItem.quantity;
          changes.push({ type: "Decreased", quantityChange, item: newItem });
          isRemoveItemAction = true; // Set the flag
        }
      }
    }

    // Check for removed items
    for (const [id, oldItem] of oldCartMap.entries()) {
      if (!newCartMap.has(id)) {
        // Case 2: Item was removed
        changes.push({ type: "Removed", item: oldItem });
        isRemoveItemAction = true; // Set the flag
      }
    }

    // --- New logic to determine the final summary flag ---
    let actionFlag = "noChange";
    if (isAddToCartAction) {
      actionFlag = "addToCart"; // "Add" actions take priority
    } else if (isRemoveItemAction) {
      actionFlag = "removeItem";
    }

    // Return both the detailed changes and the summary flag
    return { ...changes?.[0], actionFlag };
  }

  window.HS_SLIDE_CART_UPDATED = function (cart) {
    const productInfo = findCartChanges(latestCartObject?.items, cart?.items);
    const item = productInfo?.item;

    if (item) {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event:
          productInfo?.actionFlag === "removeItem"
            ? "remove_from_cart"
            : "add_to_cart",
        total_qty: cart?.item_count,
        ...commonData,
        ecommerce: {
          currency: cart?.currency || "NA",
          value: cart?.items_subtotal_price / 100.0 || 0,
          items: [
            {
              item_id: item?.id || item?.variant_id || "NA",
              item_name: item?.product_title || "NA",
              coupon: "NA",
              discount: (item.original_price - item.final_price) / 100.0 || 0,
              index: 0,
              item_brand: item?.vendor || "GlambyRivaaz",
              item_category: item?.product_type || "NA",
              item_category2: "NA",
              item_category3: "NA",
              item_category4: "NA",
              item_category5: "NA",
              item_list_id: "cart_drawer",
              item_list_name: "Cart Drawer",
              item_variant: item?.variant_title || "NA",
              price: item?.final_price / 100.0 || 0,
              quantity: item?.quantity || 1,
            },
          ],
        },
      });
    }

    latestCartObject = cart;
  };

  window.HS_SLIDE_CART_OPENED = function () {
    const cartItems =
      latestCartObject?.items?.length > 0
        ? latestCartObject?.items?.map((item, index) => ({
            item_id: item?.id || item?.variant_id || "NA",
            item_name: item?.product_title || "NA",
            coupon: "NA",
            discount:
              item?.original_line_price > item?.original_price
                ? (item?.original_line_price - item?.original_price || 0) /
                  100.0
                : 0,
            index: index,
            item_brand: item?.vendor || "GlambyRivaaz",
            item_category: "NA",
            item_category2: "NA",
            item_category3: "NA",
            item_category4: "NA",
            item_category5: "NA",
            item_list_id: "cart_drawer",
            item_list_name: "Cart Drawer",
            item_variant: item?.variant_title || "NA",
            price: item?.original_price / 100.0 || 0,
            quantity: item?.quantity || "1",
          }))
        : [];

    dataLayer.push({ ecommerce: null });
    dataLayer.push({
      event: "view_cart",
      total_qty: latestCartObject?.item_count || 0,
      ...commonData,
      ecommerce: {
        currency: latestCartObject?.currency,
        value: (latestCartObject?.items_subtotal_price || 0) / 100.0 || 0,
        items: cartItems,
      },
    });
  };

  window.HS_SLIDE_CART_CHECKOUT_CLICKED = function () {
    if (
      latestCartObject &&
      latestCartObject?.items &&
      latestCartObject?.items?.length > 0
    ) {
      const cartItems = latestCartObject.items.map((item, index) => ({
        item_id: item?.id || item?.variant_id || "NA",
        item_name: item?.product_title || "NA",
        coupon: "NA",
        discount:
          item?.original_line_price > item?.original_price
            ? (item?.original_line_price - item?.original_price || 0) / 100.0
            : 0,
        index: index,
        item_brand: item?.vendor || "GlambyRivaaz",
        item_category: "NA",
        item_category2: "NA",
        item_category3: "NA",
        item_category4: "NA",
        item_category5: "NA",
        item_list_id: "cart_drawer",
        item_list_name: "Cart Drawer",
        item_variant: item?.variant_title || "NA",
        price: item?.original_price / 100.0 || 0,
        quantity: item?.quantity || "1",
      }));
      const couponCode =
        latestCartObject?.discount_codes &&
        latestCartObject?.discount_applications?.length > 0
          ? latestCartObject?.discount_applications?.[0]?.code
          : "NA";

      dataLayer.push({ ecommerce: null });
      dataLayer.push({
        event: "begin_checkout",
        total_qty: latestCartObject?.item_count,
        ...commonData,
        ecommerce: {
          currency: latestCartObject?.currency,
          value: latestCartObject?.items_subtotal_price / 100.0 || 0,
          coupon: couponCode,
          items: cartItems,
        },
      });
    }
  };
  // HS - Slide Cart Drawer events end
});

document.addEventListener("cart:change", function (e) {
  if (e.detail && e.detail.baseEvent === "variant:add") {
    window.dataLayer.push({
      event: "add_to_cart",
      ...commonData,
      ecommerce: currentProductEcomData,
    });
  }
});
